// usePromoTimer.ts
// This hook centralizes all promotional timers logic
// It exposes:
// - activePromo: which promo is currently active
// - promoTimeLeft: remaining time in milliseconds
// - formatTime: helper to format countdown display

import { useEffect, useState, useCallback } from 'react';

export type ActivePromo =
  | 'none'
  | 'blackFriday'
  | 'cyberMonday'
  | 'newYear';

export const usePromoTimer = () => {
  const [promoTimeLeft, setPromoTimeLeft] = useState<number>(0);
  const [activePromo, setActivePromo] = useState<ActivePromo>('none');

  useEffect(() => {
    // -----------------------------
    // BLACK FRIDAY
    // Nov 26 → Nov 28, 2025 (UTC)
    // -----------------------------
    const blackFridayStart = Date.UTC(2025, 10, 26, 0, 0, 0);
    const blackFridayEnd = Date.UTC(2025, 10, 28, 22, 59, 59);

    // -----------------------------
    // CYBER MONDAY
    // Dec 11 → Dec 24, 2025 (UTC)
    // -----------------------------
    const cyberMondayStart = Date.UTC(2025, 11, 11, 0, 0, 0);
    const cyberMondayEnd = Date.UTC(2025, 11, 24, 22, 59, 59);

    // -----------------------------
    // NEW YEAR OFFER
    // Dec 26, 2025 → Jan 15, 2026 (UTC)
    // -----------------------------
    const newYearStart = Date.UTC(2025, 11, 26, 0, 0, 0);
    const newYearEnd = Date.UTC(2026, 0, 31, 22, 59, 59);

    const updateTimer = () => {
      const nowUtc = Date.now();

      if (nowUtc >= blackFridayStart && nowUtc <= blackFridayEnd) {
        setActivePromo('blackFriday');
        setPromoTimeLeft(blackFridayEnd - nowUtc);
      } else if (nowUtc >= cyberMondayStart && nowUtc <= cyberMondayEnd) {
        setActivePromo('cyberMonday');
        setPromoTimeLeft(cyberMondayEnd - nowUtc);
      } else if (nowUtc >= newYearStart && nowUtc <= newYearEnd) {
        setActivePromo('newYear');
        setPromoTimeLeft(newYearEnd - nowUtc);
      } else {
        setActivePromo('none');
        setPromoTimeLeft(0);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, []);

  // Formats milliseconds into: "Xd Xh Xm Xs"
  const formatTime = useCallback((milliseconds: number) => {
    if (milliseconds <= 0) return '0j 0h 0m 0s';

    const totalSeconds = Math.floor(milliseconds / 1000);
    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${days}j ${hours}h ${minutes}m ${seconds}s`;
  }, []);

  return { activePromo, promoTimeLeft, formatTime };
};
